import React, { FormEvent, useMemo, useReducer, useState } from "react";
import { ItemCategoryModel } from "../models/ItemCategoryModel";
import itemService from "../services/itemService";
import { Link, useLocation } from "react-router-dom";
import { INewItem, ItemImageFile, ItemModel } from "../models/ItemModel";
import { useAppContext } from "../context/AppContext";
import ItemImageUploadComp from "../components/ItemImageUploadComp";
import { useCategorySelector } from "../hooks/useItemCategorySelector";
import { useImageUpload } from "../hooks/useImageUpload";
import { usePrimaryImageHandler } from "../hooks/usePrimaryImageHandler";
import itemFormReducer, { ItemFormState, ItemValidatonErrors } from "../reducers/itemFormReducer";



const AddItemPage = () => {

    const location = useLocation();
    const { categories } = location.state || { categories: [] };

    const { seller } = useAppContext();
    const [sellerId] = useState(seller!.sellerId); //seller id for filtering items
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const defaultItem: INewItem = useMemo(
        () => ({
            itemID: 0,
            sellerID: sellerId,
            categoryID: 0,
            itemName: '',
            primaryImage: '',
            price: 0,
            description: '',
            minOfferQty: 0,
            bulkDiscountEnabled: false,
            bulkMinQty: 0,
            bulkDiscount: 0,
            infoLinkTitle: '',
            infoLinkUrl: '',
            infoLinkText: '',
            availableRegion: ''
        }), [sellerId]);

    const initialState: ItemFormState = {
        item: new ItemModel({ ...defaultItem }),
        errors: {},
        isSubmitting: false,
        isDeleting: false,
        serviceSuccess: "",
        serviceError: "",
    };

    const [state, dispatch] = useReducer(itemFormReducer, initialState);

    // Category dropdown state and functions
    const { selectedRootCategory, setSelectedRootCategory, selectedSubCategory, setSelectedSubCategory, subCategories, getSubCategories, initCategoryDropdown } = useCategorySelector(categories);
    // Image upload state and functions
    const { images, setImages, uploadItemImages, uploadProgress, setUploadProgress } = useImageUpload();
    // Primary image handler
    const { updatePrimaryImage } = usePrimaryImageHandler();

    const validateItemForm = () => {
        const errors: ItemValidatonErrors = {};
        const { itemName, description, price } = state.item;

        if (!itemName) {
            errors.itemName = "Item Name is required";
        }
        else if (itemName.length < 3) {
            errors.itemName = "Item Name must be at least 3 characters long";
        }

        if (!description) {
            errors.description = "Description is required";
        }

        if (price === null || price === undefined || isNaN(price)) {
            errors.price = "Price is required";
        }
        else if (price < 0) {
            errors.price = "Price must be greater than or equal to 0";
        }

        if (!selectedRootCategory) {
            errors.rootCategory = "Category is required";
        }

        if (subCategories && subCategories.length > 0 && (!selectedSubCategory || !state.item.categoryId)) {
            errors.subCategory = "Subcategory is required";
        }

        if (images.length === 0) {
            errors.primaryImage = "At least one photo is required";
        }

        dispatch({ type: "SET_ERRORS", payload: errors });
        return Object.keys(errors).length === 0;
    }

    const handleReset = () => {
        dispatch({ type: "RESET_ITEM", payload: new ItemModel({ ...defaultItem }) });
        setImages([]);
        setSelectedRootCategory(null);
        setSelectedSubCategory(null);
        setIsSubmitted(false);
    }

    const handleCreateItemSubmit = async (e: FormEvent) => {
        e.preventDefault();
        dispatch({ type: "SET_ERROR", payload: "" });
        dispatch({ type: "SET_SUCCESS", payload: "" });

        if (validateItemForm()) {
            dispatch({ type: "SET_SUBMITTING", payload: true });
            try {
                const result = await itemService.createItem(state.item);
                if (result.result === "Success") {
                    const createdItem = result.item;

                    if (createdItem) {
                        createdItem.primaryImage = state.item.primaryImage; // Ensure primary image is set
                        // Upload images if any
                        const filesToUpload = images.filter((img): img is ItemImageFile => img instanceof ItemImageFile).map(img => img.file);
                        if (filesToUpload && filesToUpload.length > 0) {
                            // Upload images and get the results
                            const uploadResults = await uploadItemImages(createdItem.itemId, filesToUpload);
                            if (!uploadResults || uploadResults.length === 0) {
                                throw new Error("Failed to upload images. Please try again.");
                            }

                            setUploadProgress(0); // Reset upload progress after upload
                            // Set the primary image if not already set
                            await updatePrimaryImage(createdItem, images, uploadResults);
                        }
                        else {
                            await updatePrimaryImage(createdItem, images);
                        }

                        dispatch({ type: "SET_SUCCESS", payload: "Item created successfully" });
                        setIsSubmitted(true);

                    }
                    else {
                        throw new Error("Failed to create item. Item is null.");
                    }
                }
                else if (result.result === "Limit") {
                    throw new Error("You have reached the maximum number of items (1000). Please delete some items before adding new ones.");
                }
                else if (result.result === "Error") {
                    throw new Error("Failed to create item. Please try again.");
                }
                else {
                    throw new Error("Unknown Error. Failed to create item. Please try again.");
                }

            } catch (error: any) {
                dispatch({ type: "SET_ERROR", payload: error?.message ?? "An unknown error occurred." });
            }
            finally {
                dispatch({ type: "SET_SUBMITTING", payload: false });
            }
        }
    }


    return (
        <div className="mt-3 mb-5">
            <div className="card rounded-0">
                <div className="card-body p-4">
                    <h5 className="card-title"> New Item</h5>
                    {!isSubmitted ? (
                        <>
                            <ItemImageUploadComp
                                item={state.item}
                                itemImages={images}
                                newItemErrors={state.errors}
                                onServiceErrorChange={(error: string) => dispatch({ type: "SET_ERROR", payload: error })}
                                onItemImagesChange={setImages}
                                onItemChange={(item: ItemModel) => dispatch({ type: "SET_ITEM", payload: item })}

                            ></ItemImageUploadComp>
                            {uploadProgress > 0 && (
                                <div className="progress mt-2" style={{ height: '20px' }}>
                                    <div
                                        className="progress-bar bg-success progress-bar-striped progress-bar-animated"
                                        role="progressbar"
                                        style={{ width: `${uploadProgress}%` }}
                                        aria-valuenow={uploadProgress}
                                        aria-valuemin={0}
                                        aria-valuemax={100}
                                    >
                                        {uploadProgress}%
                                    </div>
                                </div>
                            )}

                            <form className="row g-3 mt-3" onSubmit={handleCreateItemSubmit}>
                                <div className="col-md-12">
                                    <label className="form-label">Name</label>
                                    <input
                                        type="text"
                                        className={`form-control ${state.errors.itemName ? 'is-invalid' : ''}`}
                                        maxLength={200}
                                        placeholder="Name of the item"
                                        value={state.item.itemName}
                                        onChange={(e) =>
                                            dispatch({ type: "SET_ITEM", payload: { itemName: e.target.value } })
                                        }
                                    />
                                    {state.errors.itemName && <div className="invalid-feedback">{state.errors.itemName}</div>}
                                </div>
                                <div className="col-md-12">
                                    <label className="form-label">Description</label>
                                    <textarea
                                        className={`form-control ${state.errors.description ? 'is-invalid' : ''}`}
                                        rows={4}
                                        maxLength={2000}
                                        placeholder="Description of the item"
                                        value={state.item.description}
                                        onChange={(e) =>
                                            dispatch({ type: "SET_ITEM", payload: { description: e.target.value } })
                                        }
                                    />
                                    {state.errors.description && <div className="invalid-feedback">{state.errors.description}</div>}

                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">Category</label>
                                    <select
                                        className={`form-control ${state.errors.rootCategory ? 'is-invalid' : ''}`}
                                        value={selectedRootCategory ?? ''}
                                        onChange={(e) => {
                                            const value = e.target.value ? parseInt(e.target.value) : null;
                                            setSelectedRootCategory(value);

                                            if (value != null) {
                                                const subCats = getSubCategories(value);
                                                if (!subCats || subCats.length == 0) {
                                                    setSelectedSubCategory(null);
                                                    dispatch({ type: "SET_ITEM", payload: { categoryId: value } });
                                                }

                                            }
                                        }}
                                    >
                                        <option value="">-- Select Category --</option>
                                        {categories.map((cat: ItemCategoryModel) => (
                                            <option key={cat.id} value={cat.id}>
                                                {cat.name}
                                            </option>
                                        ))}
                                    </select>
                                    {state.errors.rootCategory && <div className="invalid-feedback">{state.errors.rootCategory}</div>}

                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Subcategory</label>
                                    <select
                                        className={`form-control ${state.errors.subCategory ? 'is-invalid' : ''}`}
                                        value={selectedSubCategory ?? ''}
                                        onChange={(e) => {
                                            const value = e.target.value ? parseInt(e.target.value) : null;
                                            setSelectedSubCategory(value);
                                            if (value)
                                                dispatch({ type: "SET_ITEM", payload: { categoryId: value } });

                                        }}
                                        disabled={subCategories.length === 0}
                                    >
                                        {(selectedRootCategory && (!subCategories || subCategories.length === 0))
                                            ? <option value="">-- N/A --</option>
                                            : <option value="">-- Select Subcategory --</option>
                                        }
                                        {subCategories.map((sub) => (
                                            <option key={sub.id} value={sub.id}>
                                                {sub.name}
                                            </option>
                                        ))}
                                    </select>
                                    {state.errors.subCategory && <div className="invalid-feedback">{state.errors.subCategory}</div>}
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label">Price</label>
                                    <input
                                        type="number"
                                        className={`form-control ${state.errors.price ? 'is-invalid' : ''}`}
                                        min={0}
                                        step="0.01"
                                        value={(state.item.price === null || state.item.price === 0) && isNaN(Number(state.item.price)) ? '' : state.item.price}
                                        onChange={(e) =>
                                            dispatch({ type: "SET_ITEM", payload: { price: parseFloat(e.target.value) } })
                                        }
                                    />
                                    {state.errors.price && <div className="invalid-feedback">{state.errors.price}</div>}
                                </div>


                                {state.serviceError && (
                                    <div className="col-md-12 clearfix">
                                        <div className="alert alert-danger p-2 mb-0">{state.serviceError}</div>
                                    </div>
                                )}
                                <div className='col-md-12 mt-3 d-flex justify-content-between'>
                                    <Link to="/items" className="btn btn-outline-dark rounded-0">Back</Link>
                                    <div className="d-flex">

                                        <button type="submit" className="btn btn-dark rounded-0" disabled={isSubmitting}>
                                            {isSubmitting ? (<><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Saving ...</>) : ("Save")}
                                        </button>
                                    </div>


                                </div>
                            </form>
                        </>) : (
                        <>
                            {state.serviceSuccess && (
                                <div className="col-md-12 clearfix">
                                    <div className="alert alert-success p-2 mb-0">{state.serviceSuccess}</div>
                                </div>
                            )}
                            <div className='col-md-12 mt-3 d-flex justify-content-between'>
                                <Link to="/items" className="btn btn-outline-dark rounded-0">Back</Link>
                                <button className="btn btn-success rounded-0" onClick={handleReset}>Create Another Item</button>
                            </div>
                        </>
                    )}



                </div>
            </div>
        </div>




    );
}

export default AddItemPage;